/**
 * Flyability engine — pure, side-effect free.
 *
 * Given the app's hourly forecast (already normalized to the same shape the
 * Outlook timeline consumes — wind in km/h, precipitation chance and cloud
 * cover in percent, temperature in °C) plus a thresholds config, this module
 * classifies each hour as "go" | "marginal" | "nogo" for paramotor flying and
 * groups contiguous "go" hours into flyable windows.
 *
 * Units (matching the forecast the app fetches from Open-Meteo):
 *  - windSpeed / windGust: km/h
 *  - precipitationChance: percent (0-100)
 *  - cloudCover: percent (0-100)
 *  - temperature: °C
 *
 * Note: the forecast exposes precipitation *probability* (precipitationChance),
 * not an accumulated amount, so flyability keys off probability.
 *
 * An optional {@link DaylightGate} overlays a hard legal constraint: any hour
 * the gate rejects (outside the legal flying window — 30 min before sunrise to
 * 30 min after sunset by default) is forced to "nogo", whatever the weather.
 */
import type { DaylightGate } from "./daylight";

export type Flyability = "go" | "marginal" | "nogo";

/**
 * One hour of the normalized forecast the engine reasons over. This is the same
 * value shape produced for the Outlook timeline (see OpenMeteoWeatherHour and
 * the NWS normalization in OutlookTable), so the engine can be driven directly
 * from either forecast source.
 */
export interface FlyabilityHour {
  /** Epoch milliseconds for the start of the hour. */
  time: number;
  /** Sustained wind, km/h. */
  windSpeed: number;
  /** Wind gust, km/h. */
  windGust: number;
  /** Precipitation probability, percent (0-100). */
  precipitationChance: number;
  /** Cloud cover, percent (0-100). */
  cloudCover: number;
  /** Temperature, °C. */
  temperature: number;
  /** CAPE in J/kg — thermal-turbulence proxy (lower = smoother air). */
  cape: number;
}

/**
 * Tunable thresholds. A value is "go" when at or below the `go` bound,
 * "marginal" when at or below the `marginal` bound, and "nogo" beyond it.
 * All bounds are inclusive.
 */
export interface FlyabilityThresholds {
  /** Sustained wind, km/h. */
  windSpeed: { go: number; marginal: number };
  /** Wind gust, km/h. */
  windGust: { go: number; marginal: number };
  /** Precipitation probability, percent. */
  precipitationChance: { go: number; marginal: number };
  /** Cloud cover, percent. */
  cloudCover: { go: number; marginal: number };
  /** Thermal turbulence as CAPE in J/kg (lower = smoother air). */
  thermal: { go: number; marginal: number };
}

export type SkillPreset = "beginner" | "intermediate" | "advanced";

/** Half-open window of contiguous "go" hours: [start, end). */
export interface FlyableWindow {
  /** Epoch ms of the first flyable hour. */
  start: number;
  /** Epoch ms of the end of the last flyable hour (start of next hour). */
  end: number;
}

export interface HourFlyability {
  time: number;
  flyability: Flyability;
}

const HOUR_MS = 60 * 60 * 1_000;

/**
 * Default thresholds per skill preset. Intermediate matches the paramotor
 * defaults in the task; beginner is more conservative, advanced more permissive.
 */
export const FLYABILITY_PRESETS: Record<SkillPreset, FlyabilityThresholds> = {
  beginner: {
    windSpeed: { go: 12, marginal: 18 },
    windGust: { go: 16, marginal: 22 },
    precipitationChance: { go: 5, marginal: 15 },
    cloudCover: { go: 60, marginal: 80 },
    thermal: { go: 100, marginal: 300 },
  },
  intermediate: {
    windSpeed: { go: 18, marginal: 25 },
    windGust: { go: 25, marginal: 32 },
    precipitationChance: { go: 10, marginal: 25 },
    cloudCover: { go: 75, marginal: 90 },
    thermal: { go: 300, marginal: 700 },
  },
  advanced: {
    windSpeed: { go: 25, marginal: 32 },
    windGust: { go: 32, marginal: 40 },
    precipitationChance: { go: 15, marginal: 35 },
    cloudCover: { go: 90, marginal: 100 },
    thermal: { go: 600, marginal: 1500 },
  },
};

export const DEFAULT_SKILL_PRESET: SkillPreset = "intermediate";

export const DEFAULT_FLYABILITY_THRESHOLDS: FlyabilityThresholds =
  FLYABILITY_PRESETS[DEFAULT_SKILL_PRESET];

/**
 * Classify a single metric against a go/marginal bound (lower is better).
 * Non-finite readings (missing data) are treated as "nogo" — we never claim a
 * window is flyable on the strength of absent data.
 */
function classifyMetric(
  value: number,
  bounds: { go: number; marginal: number },
): Flyability {
  if (!Number.isFinite(value)) return "nogo";
  if (value <= bounds.go) return "go";
  if (value <= bounds.marginal) return "marginal";
  return "nogo";
}

const RANK: Record<Flyability, number> = { go: 0, marginal: 1, nogo: 2 };

/**
 * The overall hour rating is the worst (most limiting) of its metrics — unless
 * a daylight gate rejects the hour, in which case it is always "nogo" (illegal
 * to fly), regardless of how good the weather is.
 */
export function classifyHour(
  hour: FlyabilityHour,
  thresholds: FlyabilityThresholds = DEFAULT_FLYABILITY_THRESHOLDS,
  daylight?: DaylightGate,
): Flyability {
  if (daylight && !daylight(hour.time)) return "nogo";

  const ratings: Flyability[] = [
    classifyMetric(hour.windSpeed, thresholds.windSpeed),
    classifyMetric(hour.windGust, thresholds.windGust),
    classifyMetric(hour.precipitationChance, thresholds.precipitationChance),
    classifyMetric(hour.cloudCover, thresholds.cloudCover),
    classifyMetric(hour.cape, thresholds.thermal),
  ];

  return ratings.reduce((worst, rating) =>
    RANK[rating] > RANK[worst] ? rating : worst,
  );
}

/** Classify every hour, preserving order. */
export function classifyHours(
  hours: FlyabilityHour[],
  thresholds: FlyabilityThresholds = DEFAULT_FLYABILITY_THRESHOLDS,
  daylight?: DaylightGate,
): HourFlyability[] {
  return hours.map((hour) => ({
    time: hour.time,
    flyability: classifyHour(hour, thresholds, daylight),
  }));
}

/**
 * Group contiguous "go" hours into windows. Hours must be one hour apart to be
 * considered contiguous — a gap (e.g. missing forecast hour) splits the window.
 */
export function findFlyableWindows(
  hours: FlyabilityHour[],
  thresholds: FlyabilityThresholds = DEFAULT_FLYABILITY_THRESHOLDS,
  daylight?: DaylightGate,
): FlyableWindow[] {
  const rated = classifyHours(hours, thresholds, daylight);
  const windows: FlyableWindow[] = [];

  let current: FlyableWindow | undefined;

  for (const { time, flyability } of rated) {
    if (flyability !== "go") {
      current = undefined;
      continue;
    }

    if (current) {
      if (time === current.end) {
        // Exactly one hour later — extend the current window.
        current.end = time + HOUR_MS;
        continue;
      }
      if (time < current.end) {
        // Duplicate or out-of-order hour already covered — keep the window.
        continue;
      }
    }

    current = { start: time, end: time + HOUR_MS };
    windows.push(current);
  }

  return windows;
}

/**
 * The next flyable window starting at or after `from` (epoch ms, default now),
 * or undefined if none. A window already in progress at `from` is returned
 * (its original start is preserved).
 */
export function nextFlyableWindow(
  hours: FlyabilityHour[],
  thresholds: FlyabilityThresholds = DEFAULT_FLYABILITY_THRESHOLDS,
  from: number = Date.now(),
  daylight?: DaylightGate,
): FlyableWindow | undefined {
  return findFlyableWindows(hours, thresholds, daylight).find(
    (window) => window.end > from,
  );
}

/** A tunable metric, used to explain what is limiting an hour's flyability. */
export type FlyabilityMetric = keyof FlyabilityThresholds;

const METRIC_VALUE: Record<FlyabilityMetric, (hour: FlyabilityHour) => number> =
  {
    windSpeed: (hour) => hour.windSpeed,
    windGust: (hour) => hour.windGust,
    precipitationChance: (hour) => hour.precipitationChance,
    cloudCover: (hour) => hour.cloudCover,
    thermal: (hour) => hour.cape,
  };

const METRIC_ORDER: FlyabilityMetric[] = [
  "windSpeed",
  "windGust",
  "precipitationChance",
  "cloudCover",
  "thermal",
];

/**
 * The single metric most limiting an hour's flyability (the binding
 * constraint), or null when every metric is "go". Ties resolve in the order
 * wind, gust, precipitation, cloud.
 */
export function limitingFactor(
  hour: FlyabilityHour,
  thresholds: FlyabilityThresholds = DEFAULT_FLYABILITY_THRESHOLDS,
): FlyabilityMetric | null {
  let worst: FlyabilityMetric | null = null;
  let worstRank = RANK.go;

  for (const metric of METRIC_ORDER) {
    const rank =
      RANK[classifyMetric(METRIC_VALUE[metric](hour), thresholds[metric])];
    if (rank > worstRank) {
      worstRank = rank;
      worst = metric;
    }
  }

  return worst;
}
